import { LanguageContext } from "@axon-enterprise/i18n-helpers";
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";

import {
  FlowEngine,
  type FlowState,
  type Step,
  type FlowEventHandler,
} from "../services/flows";
import { postMessageService } from "../services/postMessage";
import type { ChatMessage } from "../services/types";
import { logger } from "../utils/logger";
import { getSupportPhoneForLocale } from "../utils/phoneFormatter";

interface FlowContextValue {
  // Flow Engine Instance
  engine: FlowEngine | null;

  // Flow State
  flowState: FlowState | null;
  currentStep: Step | null;
  isFlowActive: boolean;
  isLoading: boolean;

  // Messages (merged flow messages with chat messages)
  messages: ChatMessage[];

  // Actions
  loadFlow: (flowId: string, variables?: Record<string, any>) => Promise<void>;
  startFlow: () => void;
  stopFlow: () => void;
  nextStep: (userInput?: any) => Promise<void>;
  selectChip: (chipId: string) => void;
  setVariable: (key: string, value: any) => void;
  getVariable: (key: string) => any;
  addMessage: (message: ChatMessage) => void;

  // Input visibility state (controlled by current step)
  inputVisibility: "both" | "text" | "plus" | "none";
}

const FlowContext = createContext<FlowContextValue | null>(null);

interface FlowProviderProps {
  children: React.ReactNode;
  initialVariables?: Record<string, any>;
  enableFallbackMode?: boolean; // Allow non-flow mode for backward compatibility
  enablePostMessage?: boolean; // Enable PostMessage API for external communication
}

const EMPTY_INITIAL_VARIABLES: Record<string, any> = {};

export const FlowProvider: React.FC<FlowProviderProps> = ({
  children,
  initialVariables = EMPTY_INITIAL_VARIABLES,
  enableFallbackMode = true,
  enablePostMessage = false,
}) => {
  const [engine, setEngine] = useState<FlowEngine | null>(null);
  const [flowState, setFlowState] = useState<FlowState | null>(null);
  const [currentStep, setCurrentStep] = useState<Step | null>(null);
  const [isFlowActive, setIsFlowActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputVisibility, setInputVisibility] = useState<
    "both" | "text" | "plus" | "none"
  >("both");
  const { language } = useContext(LanguageContext);

  const engineRef = useRef<FlowEngine | null>(null);

  // Initialize flow engine
  useEffect(() => {
    const flowEngine = new FlowEngine();
    setEngine(flowEngine);
    engineRef.current = flowEngine;

    // Set up event listeners
    const onStepChanged: FlowEventHandler<"stepChanged"> = event => {
      logger.debug("[FlowProvider] Step changed", {
        from: event.from?.id,
        to: event.to.id,
      });
      setCurrentStep(event.to);

      // Update input visibility based on step configuration
      if (event.to.inputVisibility) {
        setInputVisibility(event.to.inputVisibility);
      }

      // Add system message for step transition if needed
      if (event.to.type === "system" && event.to.text) {
        const systemMessage: ChatMessage = {
          from: "bot",
          text: event.to.text,
          stepId: event.to.id,
        };
        setMessages(prev => [...prev, systemMessage]);
      }
    };

    const onFlowStarted: FlowEventHandler<"flowStarted"> = event => {
      logger.info("[FlowProvider] Flow started", { flowId: event.flow.id });
      setIsFlowActive(true);
      setFlowState(engineRef.current?.getState() || null);

      // Inject formatted support phone number based on current locale when flow starts
      const formattedSupportPhone = getSupportPhoneForLocale(language);
      engineRef.current?.setVariable('supportPhone', formattedSupportPhone);

      // Add welcome message if flow has one
      if (event.flow.steps.length > 0) {
        const firstStep = event.flow.steps[0];
        if (firstStep.type === "system" && firstStep.text) {
          const welcomeMessage: ChatMessage = {
            from: "bot",
            text: firstStep.text,
            stepId: firstStep.id,
          };
          setMessages(prev => [...prev, welcomeMessage]);
        }
      }
    };

    const onFlowFinished: FlowEventHandler<"flowFinished"> = event => {
      logger.info("[FlowProvider] Flow finished", { flowId: event.flow.id });
      setIsFlowActive(false);
      setCurrentStep(null);

      // Reset to default input visibility
      setInputVisibility("both");
    };

    const onInputRequested: FlowEventHandler<"inputRequested"> = event => {
      logger.debug("[FlowProvider] Input requested for step", {
        stepId: event.step.id,
      });

      // Update input visibility based on step requirements
      if (event.step.inputVisibility) {
        setInputVisibility(event.step.inputVisibility);
      }
    };

    const onVariableChanged: FlowEventHandler<"variableChanged"> = event => {
      logger.debug("[FlowProvider] Variable changed", {
        key: event.key,
        value: event.value,
      });
      setFlowState(engineRef.current?.getState() || null);
    };

    const onError: FlowEventHandler<"error"> = event => {
      logger.error("[FlowProvider] Flow error", {
        message: event.message,
        details: event.details,
      });

      // Add error message to chat
      const errorMessage: ChatMessage = {
        from: "bot",
        text: __("Sorry, I encountered an error. Please try again."),
        error: event.message,
      };
      setMessages(prev => [...prev, errorMessage]);

      setIsLoading(false);
    };

    const onStorylineSync: FlowEventHandler<"storylineSync"> = event => {
      logger.debug("[FlowProvider] Storyline sync requested", {
        variables: event.variables,
      });

      // Send variable sync to Storyline if PostMessage is enabled
      if (enablePostMessage && postMessageService.isEmbedded()) {
        postMessageService.sendVariableSync(event.variables);
      }
    };

    const onChipAction: FlowEventHandler<"chipAction"> = event => {
      logger.debug("[FlowProvider] Chip action triggered", {
        action: event.action,
        data: event.data,
      });

      // Handle transformToUser effect - add chip label as user message
      if (event.data?.transformToUser && event.data?.chip) {
        const userMessage: ChatMessage = {
          from: "user",
          text: __(event.data.chip.label),
        };
        setMessages(prev => [...prev, userMessage]);
        logger.info("[FlowProvider] Added chip as user message", {
          label: event.data.chip.label,
        });
      }
    };

    // Register event listeners
    flowEngine.on("stepChanged", onStepChanged);
    flowEngine.on("flowStarted", onFlowStarted);
    flowEngine.on("flowFinished", onFlowFinished);
    flowEngine.on("inputRequested", onInputRequested);
    flowEngine.on("variableChanged", onVariableChanged);
    flowEngine.on("error", onError);
    flowEngine.on("storylineSync", onStorylineSync);
    flowEngine.on("chipAction", onChipAction);

    return () => {
      // Cleanup event listeners
      flowEngine.off("stepChanged", onStepChanged);
      flowEngine.off("flowStarted", onFlowStarted);
      flowEngine.off("flowFinished", onFlowFinished);
      flowEngine.off("inputRequested", onInputRequested);
      flowEngine.off("variableChanged", onVariableChanged);
      flowEngine.off("error", onError);
      flowEngine.off("storylineSync", onStorylineSync);
      flowEngine.off("chipAction", onChipAction);
    };
  }, [enablePostMessage, language]);

  const loadFlow = useCallback(
    async (flowId: string, variables: Record<string, any> = {}) => {
      if (!engine) {
        throw new Error("Flow engine not initialized");
      }

      setIsLoading(true);
      try {
        await engine.loadFlow(flowId);

        // Inject formatted support phone number based on current locale
        const formattedSupportPhone = getSupportPhoneForLocale(language);

        // Set initial variables (including supportPhone)
        Object.entries({ 
          ...initialVariables, 
          ...variables,
          supportPhone: formattedSupportPhone 
        }).forEach(
          ([key, value]) => {
            engine.setVariable(key, value);
          }
        );

        setFlowState(engine.getState());
        logger.info("[FlowProvider] Flow loaded successfully", { flowId });
      } catch (error) {
        logger.error("[FlowProvider] Failed to load flow", error as Error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [engine, initialVariables, language]
  );

  const startFlow = useCallback(() => {
    if (!engine) {
      throw new Error("Flow engine not initialized");
    }

    engine.start();
  }, [engine]);

  const stopFlow = useCallback(() => {
    if (!engine) return;

    engine.stop();
    setIsFlowActive(false);
    setCurrentStep(null);
    setInputVisibility("both");
  }, [engine]);

  const nextStep = useCallback(
    async (userInput?: any) => {
      if (!engine) {
        throw new Error("Flow engine not initialized");
      }

      setIsLoading(true);
      try {
        await engine.next(userInput);
        setFlowState(engine.getState());
      } catch (error) {
        logger.error(
          "[FlowProvider] Failed to proceed to next step",
          error as Error
        );
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [engine]
  );

  const selectChip = useCallback(
    (chipId: string) => {
      if (!engine) {
        throw new Error("Flow engine not initialized");
      }

      try {
        engine.selectChip(chipId);
        setFlowState(engine.getState());
      } catch (error) {
        logger.error("[FlowProvider] Failed to select chip", error as Error);
        throw error;
      }
    },
    [engine]
  );

  const setVariable = useCallback(
    (key: string, value: any) => {
      if (!engine) return;
      engine.setVariable(key, value);
      setFlowState(engine.getState());
    },
    [engine]
  );

  const getVariable = useCallback(
    (key: string): any => {
      if (!engine) return undefined;
      return engine.getVariable(key);
    },
    [engine]
  );

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => {
      // Always remove ALL temporary messages before adding any new message
      // This prevents duplicate messages and ensures clean state
      const filteredMessages = prev.filter(msg => !msg.temporary);

      // If the new message is temporary, add it to the filtered list
      if (message.temporary) {
        return [...filteredMessages, message];
      }

      // For non-temporary messages, just add to the filtered list
      return [...filteredMessages, message];
    });
  }, []);

  // Set up PostMessage communication if enabled
  useEffect(() => {
    if (!enablePostMessage) return;

    // Start listening for messages
    postMessageService.startListening();

    // Handle incoming flow control messages
    const cleanupFlowInit = postMessageService.on(
      "flow:init",
      async payload => {
        logger.debug("[FlowProvider] Received flow:init message", { payload });
        try {
          await loadFlow(payload.flowId, payload.variables);
        } catch (error) {
          logger.error(
            "[FlowProvider] Failed to initialize flow from PostMessage",
            error as Error
          );
          postMessageService.sendError(`Failed to initialize flow: ${error}`);
        }
      }
    );

    const cleanupFlowStart = postMessageService.on("flow:start", payload => {
      logger.debug("[FlowProvider] Received flow:start message", { payload });
      try {
        startFlow();
      } catch (error) {
        logger.error(
          "[FlowProvider] Failed to start flow from PostMessage",
          error as Error
        );
        postMessageService.sendError(`Failed to start flow: ${error}`);
      }
    });

    const cleanupFlowStop = postMessageService.on("flow:stop", payload => {
      logger.debug("[FlowProvider] Received flow:stop message", { payload });
      try {
        stopFlow();
      } catch (error) {
        logger.error(
          "[FlowProvider] Failed to stop flow from PostMessage",
          error as Error
        );
        postMessageService.sendError(`Failed to stop flow: ${error}`);
      }
    });

    const cleanupVariableUpdate = postMessageService.on(
      "variable:update",
      payload => {
        logger.debug("[FlowProvider] Received variable:update message", {
          payload,
        });
        try {
          setVariable(payload.key, payload.value);
        } catch (error) {
          logger.error(
            "[FlowProvider] Failed to update variable from PostMessage",
            error as Error
          );
          postMessageService.sendError(`Failed to update variable: ${error}`);
        }
      }
    );

    const cleanupVariableSync = postMessageService.on(
      "variable:sync",
      payload => {
        logger.debug("[FlowProvider] Received variable:sync message", {
          payload,
        });
        try {
          Object.entries(payload.variables).forEach(([key, value]) => {
            setVariable(key, value);
          });
        } catch (error) {
          logger.error(
            "[FlowProvider] Failed to sync variables from PostMessage",
            error as Error
          );
          postMessageService.sendError(`Failed to sync variables: ${error}`);
        }
      }
    );

    return () => {
      // Cleanup PostMessage handlers
      cleanupFlowInit();
      cleanupFlowStart();
      cleanupFlowStop();
      cleanupVariableUpdate();
      cleanupVariableSync();
      postMessageService.stopListening();
    };
  }, [enablePostMessage, loadFlow, startFlow, stopFlow, setVariable]);

  // Send flow state updates via PostMessage
  useEffect(() => {
    if (!enablePostMessage || !flowState || !currentStep) return;

    postMessageService.sendFlowState(
      flowState.flow?.id || "",
      currentStep.id,
      isFlowActive,
      flowState.variables || {}
    );
  }, [enablePostMessage, flowState, currentStep, isFlowActive]);

  const contextValue: FlowContextValue = {
    engine,
    flowState,
    currentStep,
    isFlowActive,
    isLoading,
    messages,
    loadFlow,
    startFlow,
    stopFlow,
    nextStep,
    selectChip,
    setVariable,
    getVariable,
    addMessage,
    inputVisibility,
  };

  return (
    <FlowContext.Provider value={contextValue}>{children}</FlowContext.Provider>
  );
};

export const useFlowContext = (): FlowContextValue => {
  const context = useContext(FlowContext);
  if (!context) {
    throw new Error("useFlowContext must be used within a FlowProvider");
  }
  return context;
};
